import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../context/authHooks';
import { getSocket, updateSocketGuestToken } from '../../shared/socket/socketClient';
import MediasoupManager from '../../shared/lib/MediasoupManager';

// Todo el ciclo de vida de una sesión pública de terminal (validación + countdown + lobby +
// Mediasoup SFU + socket + chat). Extraído de PublicSessionView al partirlo (integración
// main→dev, epic B1). El componente solo pinta lo que devuelve este hook.
export function useTerminalSession(sessionId, spaceSlug) {
    const navigate = useNavigate();
    const authUser = useAuthUser();

    // Estado de validación de la sesión
    const [loading, setLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);
    const [expired, setExpired] = useState(false);
    const [error, setError] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState('');

    // Lobby de pre-ingreso
    const [joined, setJoined] = useState(false);
    const [participantName, setParticipantName] = useState('');
    const [joiningSession, setJoiningSession] = useState(false);

    // Estado de media
    const [micEnabled, setMicEnabled] = useState(true);
    const [camEnabled, setCamEnabled] = useState(true);
    const [screenSharing, setScreenSharing] = useState(false);

    // Streams local y remotos
    const [localStream, setLocalStream] = useState(null);
    const [peers, setPeers] = useState([]);
    const [remoteStreams, setRemoteStreams] = useState({}); // peerId -> MediaStream
    const [selectedSpeakerId, setSelectedSpeakerId] = useState('local');

    // Chat
    const [chatMessages, setChatMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');

    // Refs
    const localVideoRef = useRef(null);
    const lobbyVideoRef = useRef(null);
    const mainVideoRef = useRef(null);
    const joinedRef = useRef(false);
    const chatEndRef = useRef(null);
    const localStreamRef = useRef(null);
    const mediasoupRef = useRef(null);
    const remoteStreamsRef = useRef({});
    const guestTokenRef = useRef(null);

    // 1. Traer y validar la sesión
    useEffect(() => {
        let isMounted = true;
        const checkSession = async () => {
            try {
                const res = await fetch(`/api/public/session/${sessionId}`);
                const data = await res.json();
                if (!isMounted) return;

                if (!res.ok || !data.success) {
                    setError(data.error || 'Sesión no encontrada');
                    setLoading(false);
                    return;
                }
                if (data.expired) {
                    setExpired(true);
                    setSessionData(data);
                    setLoading(false);
                    return;
                }

                setSessionData(data);
                if (data.session?.chatLog && Array.isArray(data.session.chatLog)) {
                    setChatMessages(data.session.chatLog);
                }
                const initialName = (authUser?.name || data.session?.guestName || '').trim();
                setParticipantName(initialName);
            } catch {
                if (isMounted) setError('Error al validar la sesión.');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        checkSession();
        return () => { isMounted = false; };
    }, [sessionId, authUser]);

    // 2. Countdown + watchdog de expiración
    useEffect(() => {
        if (!sessionData?.session?.endTime) return;

        const updateClock = () => {
            const now = new Date().getTime();
            const start = sessionData.session.startTime ? new Date(sessionData.session.startTime).getTime() : now;
            const end = new Date(sessionData.session.endTime).getTime();

            if (now >= end) {
                setExpired(true);
                setTimeRemaining('00:00 - Finalizada');
                if (localStreamRef.current) {
                    localStreamRef.current.getTracks().forEach((t) => t.stop());
                }
                if (mediasoupRef.current) {
                    mediasoupRef.current.destroy();
                }
                fetch(`/api/public/session/${sessionId}/end`, { method: 'POST' }).catch(() => {});
                return false;
            }

            if (now < start) {
                const diffStart = start - now;
                const hours = Math.floor(diffStart / (1000 * 60 * 60));
                const minutes = Math.floor((diffStart % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diffStart % (1000 * 60)) / 1000);
                setTimeRemaining(hours > 0
                    ? `Inicia en ${hours}h ${minutes}m`
                    : `Inicia en ${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
            } else {
                const diffEnd = end - now;
                const hours = Math.floor(diffEnd / (1000 * 60 * 60));
                const minutes = Math.floor((diffEnd % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diffEnd % (1000 * 60)) / 1000);
                setTimeRemaining(hours > 0
                    ? `${hours}h ${minutes}m ${seconds}s`
                    : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
            }
            return true;
        };

        updateClock();
        const interval = setInterval(() => {
            if (!updateClock()) clearInterval(interval);
        }, 1000);
        return () => clearInterval(interval);
    }, [sessionData?.session?.startTime, sessionData?.session?.endTime, sessionId]);

    // 3. Preview de cámara/micrófono en el lobby
    useEffect(() => {
        if (joined || expired || loading) return;

        let active = true;
        navigator.mediaDevices.getUserMedia({
            audio: true,
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        }).then((stream) => {
            if (!active) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            setLocalStream(stream);
            localStreamRef.current = stream;
            if (lobbyVideoRef.current) {
                lobbyVideoRef.current.srcObject = stream;
            }
        }).catch((err) => {
            console.warn('Could not get media preview in lobby:', err);
        });

        return () => { active = false; };
    }, [joined, expired, loading]);

    useEffect(() => {
        if (lobbyVideoRef.current && localStream) {
            lobbyVideoRef.current.srcObject = localStream;
        }
    }, [localStream, joined]);

    const cleanupListenersRef = useRef(null);

    // 5. Tracks remotos desde el SFU
    const handleRemoteTrack = useCallback((peerId, track, kind) => {
        const current = remoteStreamsRef.current[peerId] || new MediaStream();
        current.getTracks().forEach((t) => {
            if (t.kind === track.kind) current.removeTrack(t);
        });
        current.addTrack(track);
        remoteStreamsRef.current[peerId] = current;

        if (kind === 'audio' || track.kind === 'audio') {
            let audioEl = document.getElementById(`remote_audio_${peerId}`);
            if (!audioEl) {
                audioEl = document.createElement('audio');
                audioEl.id = `remote_audio_${peerId}`;
                audioEl.autoplay = true;
                document.body.appendChild(audioEl);
            }
            audioEl.srcObject = current;
        }

        setRemoteStreams((prev) => ({ ...prev, [peerId]: current }));
    }, []);

    // 4. Unirse a la reunión
    const handleJoinSession = async (e) => {
        if (e) e.preventDefault();
        const trimmedName = participantName.trim();
        if (!trimmedName) return;

        setJoiningSession(true);
        try {
            let socket;
            if (!authUser?.isDbConnected) {
                const tokenRes = await fetch(`/api/public/session/${sessionId}/guest-token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: trimmedName }),
                });
                const tokenData = await tokenRes.json();
                if (tokenData.success && tokenData.token) {
                    guestTokenRef.current = tokenData.token;
                    socket = updateSocketGuestToken(tokenData.token);
                } else {
                    socket = getSocket();
                }
            } else {
                socket = getSocket();
            }

            let stream = localStreamRef.current;
            if (!stream) {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: { width: { ideal: 1280 }, height: { ideal: 720 } },
                });
                setLocalStream(stream);
                localStreamRef.current = stream;
            }
            if (stream) {
                const aTrack = stream.getAudioTracks()[0];
                if (aTrack) aTrack.enabled = micEnabled;
                const vTrack = stream.getVideoTracks()[0];
                if (vTrack) vTrack.enabled = camEnabled;
            }

            const mediasoup = new MediasoupManager(socket, (peerId, track, kind) => {
                handleRemoteTrack(peerId, track, kind);
            });

            try {
                await mediasoup.init();
                mediasoupRef.current = mediasoup;

                let audioProducerId = null;
                let videoProducerId = null;
                if (micEnabled && stream.getAudioTracks()[0]) {
                    audioProducerId = await mediasoup.publishAudio(stream.getAudioTracks()[0]);
                }
                if (camEnabled && stream.getVideoTracks()[0]) {
                    videoProducerId = await mediasoup.publishVideo(stream.getVideoTracks()[0]);
                }

                // REGISTRAR LISTENERS AQUÍ PARA EVITAR RACE CONDITIONS CON LA RESPUESTA DEL SERVIDOR
                const consumePeerProducers = (peer) => {
                    if (!mediasoup || !peer || peer.id === socket.userId) return;
                    if (peer.audioProducerId && peer.audio) mediasoup.consume(peer.id, peer.audioProducerId, 'audio').catch(() => {});
                    else mediasoup.closeConsumer(peer.id, 'audio');
                    
                    if (peer.videoProducerId && peer.video) mediasoup.consume(peer.id, peer.videoProducerId, 'video').catch(() => {});
                    else mediasoup.closeConsumer(peer.id, 'video');
                    
                    if (peer.screenProducerId && peer.screen) mediasoup.consume(peer.id, peer.screenProducerId, 'screen').catch(() => {});
                    else mediasoup.closeConsumer(peer.id, 'screen');
                };

                const handlePeers = (data) => {
                    const peerList = data.peers || [];
                    setPeers(peerList);
                    peerList.forEach((p) => consumePeerProducers(p));
                };

                const handlePeerJoined = ({ peer }) => {
                    setPeers((prev) => {
                        if (prev.find(p => p.id === peer.id)) return prev;
                        return [...prev, peer];
                    });
                    consumePeerProducers(peer);
                    setChatMessages((prev) => [...prev, {
                        id: Date.now().toString(),
                        system: true,
                        text: `${peer.name} se ha unido a la reunión.`,
                    }]);
                };

                const handlePeerUpdated = ({ peer }) => {
                    setPeers((prev) => prev.map((p) => (p.id === peer.id ? { ...p, ...peer } : p)));
                    consumePeerProducers(peer);
                };

                const handlePeerLeft = ({ peerId }) => {
                    setPeers((prev) => prev.filter((p) => p.id !== peerId));
                    setSelectedSpeakerId((cur) => (cur === peerId ? 'local' : cur));
                    const audioEl = document.getElementById(`remote_audio_${peerId}`);
                    if (audioEl) audioEl.remove();
                    if (mediasoupRef.current) {
                        mediasoupRef.current.closeConsumer(peerId, 'audio');
                        mediasoupRef.current.closeConsumer(peerId, 'video');
                        mediasoupRef.current.closeConsumer(peerId, 'screen');
                    }
                };

                const handleChatMessage = (msg) => {
                    setChatMessages((prev) => [...prev, msg]);
                    setTimeout(() => {
                        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 50);
                };

                const handleChatHistory = (data) => {
                    if (data?.messages && Array.isArray(data.messages)) {
                        setChatMessages((prev) => [...data.messages, ...prev.filter(m => m.system)]);
                        setTimeout(() => {
                            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                    }
                };

                // Remover listeners anteriores por si acaso
                if (cleanupListenersRef.current) cleanupListenersRef.current();

                socket.on('terminal_session_peers', handlePeers);
                socket.on('terminal_session_peer_joined', handlePeerJoined);
                socket.on('terminal_session_peer_updated', handlePeerUpdated);
                socket.on('terminal_session_peer_left', handlePeerLeft);
                socket.on('terminal_session_chat_message', handleChatMessage);
                socket.on('terminal_session_chat_history', handleChatHistory);

                cleanupListenersRef.current = () => {
                    socket.off('terminal_session_peers', handlePeers);
                    socket.off('terminal_session_peer_joined', handlePeerJoined);
                    socket.off('terminal_session_peer_updated', handlePeerUpdated);
                    socket.off('terminal_session_peer_left', handlePeerLeft);
                    socket.off('terminal_session_chat_message', handleChatMessage);
                    socket.off('terminal_session_chat_history', handleChatHistory);
                };

                socket.emit('join_terminal_session', {
                    sessionId,
                    spaceSlug,
                    user: {
                        name: trimmedName,
                        image: authUser?.image || null,
                        audio: micEnabled,
                        video: camEnabled,
                        screen: false,
                        audioProducerId,
                        videoProducerId,
                    },
                });
            } catch (mediaErr) {
                console.warn('[Mediasoup] Error initializing SFU, joining room directly:', mediaErr);
                
                // Fallback same listener logic for direct room join
                const handleChatHistoryFallback = (data) => {
                    if (data?.messages && Array.isArray(data.messages)) {
                        setChatMessages((prev) => [...data.messages, ...prev.filter(m => m.system)]);
                    }
                };
                
                if (cleanupListenersRef.current) cleanupListenersRef.current();
                socket.on('terminal_session_chat_history', handleChatHistoryFallback);
                
                cleanupListenersRef.current = () => {
                    socket.off('terminal_session_chat_history', handleChatHistoryFallback);
                };

                socket.emit('join_terminal_session', {
                    sessionId,
                    spaceSlug,
                    user: {
                        name: trimmedName,
                        image: authUser?.image || null,
                        audio: micEnabled,
                        video: camEnabled,
                        screen: false,
                    },
                });
            }

            setJoined(true);
            joinedRef.current = true;
        } catch (err) {
            console.error('Error joining session:', err);
            setError('No se pudo conectar a la videollamada.');
        } finally {
            setJoiningSession(false);
        }
    };

    // 6. Limpieza al desmontar
    useEffect(() => {
        return () => {
            if (cleanupListenersRef.current) {
                cleanupListenersRef.current();
            }
            if (joinedRef.current) {
                const socket = getSocket(guestTokenRef.current);
                socket.emit('leave_terminal_session');
            }
            if (mediasoupRef.current) {
                mediasoupRef.current.destroy();
            }
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    // 7. Mantener el video principal y el mini-video local sincronizados
    useEffect(() => {
        if (!mainVideoRef.current) return;
        if (selectedSpeakerId === 'local') {
            mainVideoRef.current.srcObject = localStream;
        } else {
            const stream = remoteStreams[selectedSpeakerId];
            if (stream) mainVideoRef.current.srcObject = stream;
        }
    }, [selectedSpeakerId, localStream, remoteStreams]);

    useEffect(() => {
        if (localVideoRef.current && localStream) {
            localVideoRef.current.srcObject = localStream;
        }
    }, [localStream, joined]);

    // 8. Toggles de media
    const toggleMic = async () => {
        const next = !micEnabled;
        setMicEnabled(next);

        let stream = localStreamRef.current || localStream;
        let track = stream ? stream.getAudioTracks()[0] : null;
        const mediasoup = mediasoupRef.current;
        const socket = getSocket();

        if (next) {
            if (!track || track.readyState === 'ended') {
                try {
                    const freshStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    track = freshStream.getAudioTracks()[0];
                    if (stream) {
                        const oldTrack = stream.getAudioTracks()[0];
                        if (oldTrack) stream.removeTrack(oldTrack);
                        stream.addTrack(track);
                    } else {
                        stream = new MediaStream([track]);
                        setLocalStream(stream);
                        localStreamRef.current = stream;
                    }
                } catch (err) {
                    console.warn('Could not acquire microphone track:', err);
                }
            }
            if (track) {
                track.enabled = true;
                if (mediasoup) {
                    try {
                        const id = await mediasoup.publishAudio(track);
                        socket.emit('terminal_session_producers_update', { audioProducerId: id, audio: true });
                    } catch (err) {
                        console.warn('Error publishing audio:', err);
                    }
                }
            }
        } else {
            if (track) track.enabled = false;
            if (mediasoup) mediasoup.stopAudio();
            socket.emit('terminal_session_producers_update', { audioProducerId: null, audio: false });
        }

        socket.emit('terminal_session_media_update', { audio: next });
    };

    const toggleCam = async () => {
        const next = !camEnabled;
        setCamEnabled(next);

        let stream = localStreamRef.current || localStream;
        let track = stream ? stream.getVideoTracks()[0] : null;
        const mediasoup = mediasoupRef.current;
        const socket = getSocket();

        if (next) {
            if (!track || track.readyState === 'ended') {
                try {
                    const freshStream = await navigator.mediaDevices.getUserMedia({
                        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
                    });
                    track = freshStream.getVideoTracks()[0];
                    if (stream) {
                        const oldTrack = stream.getVideoTracks()[0];
                        if (oldTrack) stream.removeTrack(oldTrack);
                        stream.addTrack(track);
                    } else {
                        stream = new MediaStream([track]);
                        setLocalStream(stream);
                        localStreamRef.current = stream;
                    }
                    if (localVideoRef.current) localVideoRef.current.srcObject = stream;
                    if (mainVideoRef.current && selectedSpeakerId === 'local') {
                        mainVideoRef.current.srcObject = stream;
                    }
                } catch (err) {
                    console.warn('Could not acquire camera track:', err);
                }
            }
            if (track) {
                track.enabled = true;
                if (mediasoup) {
                    try {
                        const id = await mediasoup.publishVideo(track);
                        socket.emit('terminal_session_producers_update', { videoProducerId: id, video: true });
                    } catch (err) {
                        console.warn('Error publishing video:', err);
                    }
                }
            }
        } else {
            if (track) track.enabled = false;
            if (mediasoup) mediasoup.stopVideo();
            socket.emit('terminal_session_producers_update', { videoProducerId: null, video: false });
        }

        socket.emit('terminal_session_media_update', { video: next });
    };

    const toggleScreenShare = async () => {
        const socket = getSocket();
        const mediasoup = mediasoupRef.current;

        if (screenSharing) {
            setScreenSharing(false);
            if (mediasoup) mediasoup.stopScreenShare();
            socket.emit('terminal_session_media_update', { screen: false });
            socket.emit('terminal_session_producers_update', { screenProducerId: null });
            setSelectedSpeakerId('local');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
            setScreenSharing(true);

            if (mediasoup) {
                const res = await mediasoup.publishScreenShare(stream);
                socket.emit('terminal_session_producers_update', { screenProducerId: res.videoProducerId });
            }
            socket.emit('terminal_session_media_update', { screen: true });

            const screenTrack = stream.getVideoTracks()[0];
            screenTrack.onended = () => {
                setScreenSharing(false);
                if (mediasoup) mediasoup.stopScreenShare();
                socket.emit('terminal_session_media_update', { screen: false });
                socket.emit('terminal_session_producers_update', { screenProducerId: null });
                setSelectedSpeakerId('local');
            };

            if (mainVideoRef.current) mainVideoRef.current.srcObject = stream;
            setSelectedSpeakerId('screen');
        } catch (err) {
            console.warn('Screen share cancelled:', err);
        }
    };

    // 9. Enviar mensaje de chat
    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;
        const socket = getSocket(guestTokenRef.current);
        socket.emit('terminal_session_chat_message', { text: inputMessage.trim() });
        setInputMessage('');
    };

    const handleLeaveCall = () => {
        if (mediasoupRef.current) mediasoupRef.current.destroy();
        if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
        navigate('/');
    };

    // Lobby: toggles "en frío" (sin SFU todavía) — solo prenden/apagan el track de preview.
    const toggleLobbyMic = () => {
        const nextVal = !micEnabled;
        setMicEnabled(nextVal);
        if (localStream) {
            const t = localStream.getAudioTracks()[0];
            if (t) t.enabled = nextVal;
        }
    };

    const toggleLobbyCam = () => {
        const nextVal = !camEnabled;
        setCamEnabled(nextVal);
        if (localStream) {
            const t = localStream.getVideoTracks()[0];
            if (t) t.enabled = nextVal;
        }
    };

    return {
        authUser,
        // estado
        loading, sessionData, expired, error, timeRemaining,
        joined, participantName, setParticipantName, joiningSession,
        micEnabled, camEnabled, screenSharing,
        localStream, peers, remoteStreams, selectedSpeakerId, setSelectedSpeakerId,
        chatMessages, inputMessage, setInputMessage,
        // refs
        localVideoRef, lobbyVideoRef, mainVideoRef, chatEndRef, guestTokenRef,
        // acciones
        handleJoinSession, toggleMic, toggleCam, toggleScreenShare,
        handleSendMessage, handleLeaveCall, toggleLobbyMic, toggleLobbyCam,
    };
}
